import React, {
  useRef, useEffect, useState,
} from 'react';
import {
  any, func, arrayOf, number, object, string,
} from 'prop-types';

import debouncer from '../../../modules/debouncer';

import RenderItem from './RenderItem';
import { useIntersectionTrigger } from '../../../hooks';

/**
 * FlatList - Why did we implement our own flat list? Flat List has a pretty nasty bug
 *  where it does not call onEndReached at the right time, we were not able to work
 *  around this bug so we had to resort to creating this component.
 *  Also we did find some thrid party implementations of FlatList specifically meant to
 *  work around this but, but upon review most had implementations which included scroll
 *  events or timeouts which raise general performance concerns.
 */
const FlatList = ({
  data,
  renderItem,
  onEndReached,
  ItemSeparatorComponent,
  itemHeight,
  style,
  testID,
}) => {
  const [availableHeight, setAvailableHeight] = useState(0);
  const [dataHeight, setDataHeight] = useState(0);
  const [displayData, setDisplayData] = useState([]);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [scrollEndIndex, setScrollEndIndex] = useState(0);
  const [paddingOffset, setPaddingOffset] = useState(0);
  const containerRef = useRef(null);

  const threshold = 50;

  const debounce = debouncer(100);

  const setEndRefCallback = useIntersectionTrigger(onEndReached, displayData && displayData !== []);

  const resizeHandler = () => {
    if (containerRef.current) {
      setAvailableHeight(containerRef.current.clientHeight);

      const scrollBottom = containerRef.current.scrollTop + availableHeight;
      setScrollEndIndex(Math.min(data.length, Math.ceil(scrollBottom / itemHeight) + threshold));
    }
  };

  const scrollHandler = event => {
    const { scrollTop } = event.target;
    const scrollBottom = scrollTop + availableHeight;
    debounce(() => {
      const start = Math.max(0, Math.floor(scrollTop / itemHeight) - threshold);
      const end = Math.min(data.length, Math.ceil(scrollBottom / itemHeight) + threshold);

      if (Math.abs(start - scrollIndex) > threshold / 4
      || Math.abs(end - scrollEndIndex) > threshold / 4) {
        setScrollEndIndex(end);
        setScrollIndex(start);
      }
    });
  };

  useEffect(() => {
    setDisplayData(data.slice(scrollIndex, scrollEndIndex));
    setPaddingOffset(itemHeight * scrollIndex);
  }, [scrollEndIndex, scrollIndex, data]);

  useEffect(() => {
    if (containerRef.current) {
      const scrollBottom = containerRef.current.scrollTop + availableHeight;

      setScrollEndIndex(Math.min(data.length, Math.ceil(scrollBottom / itemHeight) + threshold));

      containerRef.current.scrollTo(0, 0);
    }

    setScrollIndex(0);
    setDataHeight(data.length * itemHeight);
  }, [data]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);

    resizeHandler();

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        ...style,
        overflowY: 'scroll',
        flex: '0 1 auto',
      }}
      onScroll={scrollHandler}
    >
      <div
        style={{
          height: dataHeight,
          paddingTop: paddingOffset,
        }}
        data-testid={testID}
      >
        {displayData.map((item, index, all) => (
          <RenderItem
            item={item}
            index={index}
            isLast={index + 1 === all.length}
            renderItem={renderItem}
            ItemSeparatorComponent={ItemSeparatorComponent}
          />
        ))}
        <div
          style={{
            width: '100%',
            height: '0px',
          }}
          ref={setEndRefCallback}
        />
      </div>
    </div>
  );
};

FlatList.propTypes = {
  onEndReached: func,
  renderItem: func.isRequired,
  ItemSeparatorComponent: func,
  data: arrayOf(any).isRequired,
  itemHeight: number,
  style: object,
  testID: string,
};

FlatList.defaultProps = {
  onEndReached: null,
  ItemSeparatorComponent: null,
  itemHeight: 0,
  style: {},
  testID: null,
};

export default FlatList;
